<script lang="ts" setup>
// Confirm for deleting a workflow from a project's Workflows tab. Deletion is permanent, so the tab
// opens this first. The parent owns the actual delete call; this is presentation + confirm/cancel.

interface Props {
  show: boolean;
  workflowName?: string;
  pending?: boolean;
  errorMessage?: string;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: "confirm"): void;
  (e: "cancel"): void;
}>();

function onBackdrop() {
  if (props.pending) return;
  emit("cancel");
}
</script>

<template>
  <div
    v-if="props.show"
    class="modal fade show d-block"
    tabindex="-1"
    @click="onBackdrop"
  >
    <div class="modal-dialog" @click.stop>
      <div class="modal-content">
        <div class="modal-header">
          <h2 class="modal-title mb-0">Delete workflow</h2>
          <button
            type="button"
            class="btn-close"
            :disabled="props.pending"
            @click="emit('cancel')"
          ></button>
        </div>
        <div class="modal-body">
          <p>
            This will permanently delete <strong>{{ props.workflowName }}</strong> and its version
            history. This can't be undone.
          </p>
          <ul class="small text-muted">
            <li>Past run history is kept — it reads on its own and isn't removed.</li>
            <li>A workflow that's running, scheduled, published in a product, or called by another workflow can't be deleted until that's resolved.</li>
          </ul>
          <div v-if="props.errorMessage" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-circle me-2"></i>
            Can't delete this workflow.
            <span> {{ props.errorMessage }}</span>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="bb-btn-secondary-cta"
            :disabled="props.pending"
            @click="emit('cancel')"
          >Cancel</button>
          <button
            type="button"
            class="bb-btn-danger"
            :disabled="props.pending"
            @click="emit('confirm')"
          >
            <span
              v-if="props.pending"
              class="spinner-border spinner-border-sm me-2"
              role="status"
            ></span>
            <i v-else class="bi bi-trash me-1"></i>
            Delete workflow
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
